/**
 * The career surface's view model (task 12.7).
 *
 * Perks are content (`content/perks.json`) and their effects are the engine's;
 * what is left for the shell is the shop. That is entirely a matter of three
 * numbers — what a perk costs, what is banked, and how many slots there are —
 * so it is derived here as plain functions and the screen stays presentational.
 *
 * The two rules the spec attaches to those numbers are both enforced in this
 * file and again in `save/document.ts`. Belt and braces, deliberately: an
 * over-full equipped set makes the *engine* throw when the next run opens, and
 * a screen that let one through would present as a mission that will not start.
 */
import type { CareerPerks, Perk } from '@managing-up/schema';
import type { CareerState } from './save/types.js';

/** One perk as the career screen draws it. */
export interface PerkRow {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly cost: number;
  readonly owned: boolean;
  readonly equipped: boolean;
  /** Banked CXP covers the cost. Meaningless once owned. */
  readonly affordable: boolean;
  /** CXP still to earn before it can be bought. Zero when it is affordable. */
  readonly shortfall: number;
  /** Owned, not equipped, and every slot is taken. */
  readonly blockedByEquipLimit: boolean;
}

export interface CareerView {
  readonly cxp: number;
  readonly equipSlots: number;
  readonly equippedCount: number;
  readonly freeSlots: number;
  readonly rows: readonly PerkRow[];
}

function rowOf(perk: Perk, career: CareerState, slotsFree: boolean): PerkRow {
  const owned = career.ownedPerkIds.includes(perk.id);
  const equipped = career.equippedPerkIds.includes(perk.id);
  return {
    id: perk.id,
    label: perk.label,
    description: perk.description,
    cost: perk.cost,
    owned,
    equipped,
    affordable: career.cxp >= perk.cost,
    shortfall: Math.max(0, perk.cost - career.cxp),
    blockedByEquipLimit: owned && !equipped && !slotsFree,
  };
}

/**
 * The whole screen, in catalogue order.
 *
 * Catalogue order rather than sorted by cost or ownership: the order is
 * authored, it is the order the engine folds effects in, and a list that
 * reshuffles as things are bought is a list nobody can learn.
 */
export function careerView(catalogue: CareerPerks, career: CareerState): CareerView {
  const equippedCount = career.equippedPerkIds.length;
  const freeSlots = Math.max(0, catalogue.equipSlots - equippedCount);
  return {
    cxp: career.cxp,
    equipSlots: catalogue.equipSlots,
    equippedCount,
    freeSlots,
    rows: catalogue.perks.map((perk) => rowOf(perk, career, freeSlots > 0)),
  };
}

/**
 * The equipped ids a run should actually open with.
 *
 * Filtered against the catalogue and truncated to the slot count, because the
 * save document outlives any one build of the perk catalogue: a perk withdrawn
 * from content, or a slot count reduced, must degrade to a run that starts.
 */
export function equippableIds(catalogue: CareerPerks, career: CareerState): readonly string[] {
  return career.equippedPerkIds
    .filter((id) => catalogue.perks.some((perk) => perk.id === id))
    .slice(0, catalogue.equipSlots);
}
